import { useState, useEffect, useCallback, useRef } from 'react';
import { API_PUBLIC_BASE } from '../utils/supabase/info';

export interface HealthStatus {
  isOnline: boolean;
  lastChecked: Date | null;
  consecutiveFailures: number;
}

const FAILURE_THRESHOLD = 3; // Krev flere påfølgende feil for å unngå falske positiver
const POLL_INTERVAL = 60000; // 60 sekunder
const INITIAL_DELAY = 5000; // 5 sekunder før første sjekk
const QUERY_TIMEOUT = 10000; // 10 sekunder timeout
const RETRY_DELAY_MS = 1200; // Kort retry ved transient feil

export function useSupabaseHealth() {
  const [healthStatus, setHealthStatus] = useState<HealthStatus>({
    isOnline: true,
    lastChecked: null,
    consecutiveFailures: 0,
  });
  const checkInFlightRef = useRef(false);

  const checkHealth = useCallback(async () => {
    // Reduser unødvendig polling i bakgrunnen (lavere risiko for falsk "offline").
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    if (checkInFlightRef.current) return;
    checkInFlightRef.current = true;

    try {
      const runSingleCheck = async (): Promise<boolean> => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), QUERY_TIMEOUT);
        try {
          const response = await fetch(
            `${API_PUBLIC_BASE}/health`,
            { signal: controller.signal, cache: 'no-store' }
          );
          return response.ok;
        } finally {
          clearTimeout(timeoutId);
        }
      };

      // Retry en gang ved transient nettverk/hiccup før vi teller det som en feil.
      let isHealthy = await runSingleCheck();
      if (!isHealthy) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        isHealthy = await runSingleCheck();
      }

      if (isHealthy) {
        // Backend fungerer!
        setHealthStatus({
          isOnline: true,
          lastChecked: new Date(),
          consecutiveFailures: 0,
        });
      } else {
        // Backend-feil
        setHealthStatus(prev => {
          const newFailures = prev.consecutiveFailures + 1;
          return {
            isOnline: newFailures < FAILURE_THRESHOLD, // Kun offline hvis >= threshold
            lastChecked: new Date(),
            consecutiveFailures: newFailures,
          };
        });
      }
    } catch (error) {
      // Network error eller timeout, med høyere terskel for offline-toast.
      setHealthStatus(prev => {
        const newFailures = prev.consecutiveFailures + 1;
        return {
          isOnline: newFailures < FAILURE_THRESHOLD, // Kun offline hvis >= threshold
          lastChecked: new Date(),
          consecutiveFailures: newFailures,
        };
      });
    } finally {
      checkInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Delay initial check to avoid false positives on app load
    const initialCheckTimeout = setTimeout(() => {
      checkHealth();
    }, INITIAL_DELAY);

    // Poll every 60 seconds
    const intervalId = setInterval(checkHealth, POLL_INTERVAL);

    return () => {
      clearTimeout(initialCheckTimeout);
      clearInterval(intervalId);
    };
  }, [checkHealth]);

  return { healthStatus, checkHealth };
}
